import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * The account-side half of the 360° view.
 *
 * The existing `user360Service` reads trademark intelligence from the search
 * cluster; these endpoints answer a different question — what this customer has
 * bought, spent, delegated and done inside the product. Different service,
 * different base URL, same screen.
 *
 * Several of the tables behind these are new. Where one has not been created in
 * an environment yet the API answers with an empty list rather than an error, so
 * a panel reads as "nothing here" and fills itself in the day the table lands.
 */

const get = async (path, params = {}) => {
  const clean = { ...params };
  Object.keys(clean).forEach((k) => {
    if (clean[k] === "" || clean[k] === undefined || clean[k] === null) delete clean[k];
  });
  const res = await axios.get(`${API_BASE_URL}/customer-360/${path}`, {
    params: clean,
    headers: authHeaders(),
  });
  return res.data || {};
};

/** Stat cards on Overview: wallet, AI credits, TM counts, team, tasks, last login. */
export const fetchCustomerSummary = (userId) => get("summary", { user_id: userId });

/** Wallet ledger, plus current/added/used totals alongside the rows. */
export const fetchWallet = (userId, page = 1, limit = 20) =>
  get("wallet", { user_id: userId, page, limit });

/** AI credit ledger — what was bought, what each call consumed. */
export const fetchAiCredits = (userId, page = 1, limit = 20) =>
  get("ai-credits", { user_id: userId, page, limit });

/** Raw API call log behind those credits: model, tokens, latency, failures. */
export const fetchAiUsage = (userId, page = 1, limit = 20) =>
  get("ai-usage", { user_id: userId, page, limit });

/** Notice replies drafted for this account, and how far each one got. */
export const fetchNoticeReplies = (userId, page = 1, limit = 20, search = "") =>
  get("notice-replies", { user_id: userId, page, limit, search });

/** Roles, who reports to whom, and the people holding them. Not paginated. */
export const fetchTeam = (userId) => get("team", { user_id: userId });

export const fetchTasks = (userId, page = 1, limit = 20, search = "") =>
  get("tasks", { user_id: userId, page, limit, search });

/** Customer-side login sessions — not CMS logins, which live elsewhere. */
export const fetchLogins = (userId, page = 1, limit = 20) =>
  get("logins", { user_id: userId, page, limit });

/**
 * Subscribed vs manually added trademarks, and which sub-user may manage what.
 */
export const fetchTmBreakdown = (userId, page = 1, limit = 20) =>
  get("tm-breakdown", { user_id: userId, page, limit });

/* The four searchable lists take a `search` term. It is filtered server-side —
   a page of twenty rows is not something worth narrowing in the browser, and the
   count under the pager has to describe the whole match, not the page. Empty
   terms are dropped by `get`, so no search reads as no parameter. */

/** The customer's own clients — the proprietors they file on behalf of. */
export const fetchClients = (userId, page = 1, limit = 20, search = "") =>
  get("clients", { user_id: userId, page, limit, search });

/** Clients bundled together, with one of them as the primary contact. */
export const fetchClientGroups = (userId, page = 1, limit = 20, search = "") =>
  get("client-groups", { user_id: userId, page, limit, search });

/** The clients inside one group, with the role each holds in it. */
export const fetchClientGroupMembers = (clientGroupId) =>
  get("client-group-members", { client_group_id: clientGroupId });

/**
 * Emails sent to one client, or to one client group.
 *
 * `recipient_type` is what separates the two — the same log holds both, and an
 * id alone would be ambiguous across the two tables.
 *
 * @param {"proprietor"|"client_group"} recipientType
 */
export const fetchClientEmailLogs = (userId, recipientId, recipientType) =>
  get("client-email-logs", {
    user_id: userId,
    recipient_id: recipientId,
    recipient_type: recipientType,
  });
